import { type FormEvent } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import Logo from '../../components/base/Logo';
import { Eye, EyeOff, Check, CheckCircle } from 'lucide-react';

function PasswordStrength({ password }: { password: string }) {
  const score = [password.length >= 8, /[A-Z]/.test(password), /[0-9]/.test(password), /[^A-Za-z0-9]/.test(password)].filter(Boolean).length;
  const colors = ['', 'bg-rose-500', 'bg-amber-500', 'bg-yellow-400', 'bg-emerald-500'];
  const labels = ['', 'Zəif', 'Orta', 'Yaxşı', 'Güclü'];
  if (!password) return null;
  return (
    <div className="mt-2">
      <div className="flex gap-1 mb-1">
        {[1,2,3,4].map(i => <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i <= score ? colors[score] : 'bg-slate-200'}`} />)}
      </div>
      <p className="text-xs text-slate-500">{labels[score]}</p>
    </div>
  );
}

export default function Register() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pw, setPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [terms, setTerms] = useState(false);
  const [confirm, setConfirm] = useState('');

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (pw !== confirm) errs.confirm = t('register.error_password');
    if (!terms) errs.terms = t('register.error_terms');
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setLoading(true);
    setTimeout(() => { setLoading(false); setSuccess(true); }, 1500);
  };

  const features = [t('register.feature1'), t('register.feature2'), t('register.feature3'), t('register.feature4')];

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-lg p-10 max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h2 className="font-display text-2xl font-bold text-slate-900 mb-2">{t('register.success')}</h2>
          <p className="text-slate-500 mb-6">{t('register.success_msg')}</p>
          <button onClick={() => navigate('/login')} className="bg-primary-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-primary-700 transition-colors">
            {t('login.btn')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="min-h-screen flex">
        {/* Left panel */}
        <div className="hidden lg:flex lg:w-5/12 gradient-hero flex-col justify-between p-10 pt-24">
          <div>
            <Logo variant="light" size="lg" />
            <h2 className="font-display text-3xl font-bold text-white mt-10 mb-4">{t('register.title')}</h2>
            <p className="text-white/60 leading-relaxed mb-10">{t('register.subtitle')}</p>
            <ul className="space-y-4">
              {features.map((f, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <Check size={12} className="text-emerald-400" />
                  </span>
                  <span className="text-white/70 text-sm">{f}</span>
                </li>
              ))}
            </ul>
          </div>
          <p className="text-white/40 text-sm">
            {t('register.already')}{' '}
            <Link to="/login" className="text-white/70 hover:text-white underline">{t('register.login_link')}</Link>
          </p>
        </div>

        {/* Right panel */}
        <div className="flex-1 flex items-center justify-center p-6 py-24 bg-slate-50">
          <div className="w-full max-w-lg">
            <div className="lg:hidden mb-8 text-center"><Logo variant="dark" size="lg" /></div>
            <h1 className="font-display text-2xl font-bold text-slate-900 mb-1">{t('register.title')}</h1>
            <p className="text-slate-500 text-sm mb-8">{t('register.subtitle')}</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('register.firstname')}</label>
                  <input type="text" required className="w-full px-4 py-3 rounded-xl border border-slate-200 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('register.lastname')}</label>
                  <input type="text" required className="w-full px-4 py-3 rounded-xl border border-slate-200 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('register.email')}</label>
                <input type="email" required placeholder={t('register.email_placeholder')} className="w-full px-4 py-3 rounded-xl border border-slate-200 text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('register.phone')}</label>
                <input type="tel" placeholder={t('register.phone_placeholder')} className="w-full px-4 py-3 rounded-xl border border-slate-200 text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('register.password')}</label>
                <div className="relative">
                  <input type={showPw ? 'text' : 'password'} required placeholder={t('register.password_placeholder')} onChange={e => setPw(e.target.value)}
                    className="w-full px-4 py-3 pr-11 rounded-xl border border-slate-200 text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <PasswordStrength password={pw} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('register.confirm_password')}</label>
                <div className="relative">
                  <input type={showConfirm ? 'text' : 'password'} required placeholder={t('register.confirm_password_placeholder')} onChange={e => setConfirm(e.target.value)}
                    className={`w-full px-4 py-3 pr-11 rounded-xl border text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all ${errors.confirm ? 'border-rose-400 bg-rose-50' : 'border-slate-200'}`} />
                  <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {errors.confirm && <p className="text-rose-500 text-xs mt-1">{errors.confirm}</p>}
              </div>
              <div>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" onChange={e => setTerms(e.target.checked)} className="mt-0.5 w-4 h-4 rounded border-slate-300 accent-primary-600" />
                  <span className="text-sm text-slate-600">{t('register.terms')} <span className="text-primary-600 cursor-pointer hover:underline">{t('register.terms_link')}</span></span>
                </label>
                {errors.terms && <p className="text-rose-500 text-xs mt-1">{errors.terms}</p>}
              </div>
              <button type="submit" disabled={loading}
                className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3.5 rounded-xl transition-all duration-200 disabled:opacity-60 text-sm mt-2">
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
                    Yüklənir...
                  </span>
                ) : t('register.btn')}
              </button>
            </form>
            <p className="text-center text-sm text-slate-500 mt-6 lg:hidden">
              {t('register.already')}{' '}
              <Link to="/login" className="text-primary-600 font-semibold">{t('register.login_link')}</Link>
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
